import { v4 as uuidv4 } from "uuid";
import type { StoreApi } from "zustand";
import {
  canAcceptMediaType,
  type Action,
  type ActionResult,
  type Clip,
  type MediaItem,
  type Project,
} from "@openreel/core";
import type { ProjectState } from "../project-store";
import { planRippleInsert } from "../../utils/timeline-item-actions";
import { calculateTimelineDuration } from "./index";

type Get = StoreApi<ProjectState>["getState"];
type Set = StoreApi<ProjectState>["setState"];

export type ClipSlice = Pick<
  ProjectState,
  | "addClip"
  | "addClipToNewTrack"
  | "addClipToTimeline"
  | "separateAudio"
  | "removeClip"
  | "moveClip"
  | "closeGapBeforeClip"
  | "moveClips"
  | "trimClip"
  | "splitClip"
  | "rippleDeleteClip"
  | "slipClip"
  | "slideClip"
  | "rollEdit"
  | "trimToPlayhead"
  | "getClip"
>;

/**
 * The length clip/add would give this media if we said nothing, restated here
 * because the ripple has to know how much room to open before the clip exists.
 * Passed back as params.duration so the two can never disagree: images and
 * graphics carry metadata.duration 0 and fall to the same 5 second default.
 */
function resolveClipDuration(mediaItem: MediaItem | undefined): number {
  const mediaDuration = mediaItem?.metadata.duration;
  return mediaDuration && mediaDuration > 0 ? mediaDuration : 5;
}

/**
 * The length to give a piece of audio being added, so a three minute song
 * dropped under a thirty second edit arrives already cut to the footage instead
 * of leaving two and a half minutes of silence for the user to trim by hand.
 *
 * The measure is the furthest end of the VIDEO clips, not the end of the whole
 * project, because a second piece of music has to be measured against the same
 * footage the first one was: count every clip and the first audio clip extends
 * the very thing the next one is trimmed to. Videos are picked out by media
 * type rather than by track type, since an image can sit on a video track.
 *
 * Returns undefined for "say nothing, let clip/add use the whole file", which
 * covers everything this rule deliberately leaves alone: media that is not
 * audio, a timeline with no video on it yet, and audio already shorter than the
 * footage, because trimming never stretches a clip.
 *
 * This is the whole of the trim-on-add behaviour. To take it back out, drop
 * this function and stop passing its result as the duration.
 *
 * Trimming alone is not enough to make music audible: a clip added to a new
 * track with no chosen position used to land after everything on the timeline,
 * so a song trimmed to the footage was still a shorter song sitting past the
 * end of it. addClipToNewTrack starts unpositioned audio at 0 for that reason,
 * and the two have to stay together.
 */
function resolveAudioTrimToVideo(
  mediaItem: MediaItem | undefined,
  project: Project,
): number | undefined {
  if (mediaItem?.type !== "audio") return undefined;

  const items = project.mediaLibrary.items;
  const videoEnd = project.timeline.tracks
    .flatMap((track) => track.clips)
    .reduce((end, clip) => {
      const clipMedia = items.find((item) => item.id === clip.mediaId);
      if (clipMedia?.type !== "video") return end;
      return Math.max(end, clip.startTime + clip.duration);
    }, 0);
  if (videoEnd <= 0) return undefined;

  const audioDuration = resolveClipDuration(mediaItem);
  return Math.min(audioDuration, videoEnd);
}

/**
 * Where a clip dropped at `startTime` actually lands, and whether what follows
 * has to move for it.
 *
 * Only a drop that would otherwise sit on top of something ripples. A drop past
 * the end of a track, or into a gap wide enough to hold it, is left exactly
 * where the user put it, so appending and the computed end-of-track placements
 * still shift nothing.
 *
 * A drop that lands INSIDE a clip has no honest shift-only answer, since a plan
 * of shifts cannot cut that clip in two. It snaps to the nearer edge of the clip
 * it landed in, which keeps the insertion point on a boundary and keeps the new
 * clip off the one it was dropped onto.
 *
 * Known gap: a transition between two clips is not revalidated when a ripple
 * pushes them apart, because adjacency is only checked at the moment a
 * transition is created. Insert between a transitioned pair and the transition
 * survives, spanning the clip that now sits between them. Dragging the same
 * pair apart by hand has always done this, so it is not new in kind, but this
 * is a new way to reach it and nothing here cleans up after it.
 */
function resolveInsertion(
  clips: readonly Clip[],
  startTime: number,
  duration: number,
): { startTime: number; ripple: boolean } {
  const endTime = startTime + duration;
  const occupied = clips.some(
    (clip) =>
      clip.startTime < endTime && clip.startTime + clip.duration > startTime,
  );
  if (!occupied) return { startTime, ripple: false };

  const straddled = clips.find(
    (clip) =>
      clip.startTime < startTime && clip.startTime + clip.duration > startTime,
  );
  if (!straddled) return { startTime, ripple: true };

  const straddledEnd = straddled.startTime + straddled.duration;
  return {
    startTime:
      startTime - straddled.startTime < straddledEnd - startTime
        ? straddled.startTime
        : straddledEnd,
    ripple: true,
  };
}

export function createClipSlice(set: Set, get: Get): ClipSlice {
  return {
    addClip: async (trackId: string, mediaId: string, startTime: number) => {
      const { project, actionExecutor, getMediaItem } = get();
      const track = project.timeline.tracks.find((t) => t.id === trackId);
      const mediaItem = getMediaItem(mediaId);
      // The trimmed length has to be the one the ripple plans around too, or
      // the gap opened ahead of the clip would not be the gap it fills.
      const duration =
        resolveAudioTrimToVideo(mediaItem, project) ??
        resolveClipDuration(mediaItem);
      // No track means clip/add will fail validation anyway; let it say so
      // rather than guessing a placement for a track that is not there.
      const placement = track
        ? resolveInsertion(track.clips, startTime, duration)
        : { startTime, ripple: false };

      const action: Action = {
        type: "clip/add",
        id: uuidv4(),
        timestamp: Date.now(),
        params: {
          trackId,
          mediaId,
          startTime: placement.startTime,
          duration,
        },
      };

      if (!placement.ripple) {
        const projectCopy = structuredClone(project);
        const result = await actionExecutor.execute(action, projectCopy);
        if (result.success) {
          set({ project: { ...projectCopy, modifiedAt: Date.now() } });
        }
        return result;
      }

      // The moves go first so no intermediate project state ever holds the
      // overlap, and they share one history group with the add so a single
      // undo takes the whole insert back rather than leaving a gap behind.
      // The moves are issued here instead of through applyRipplePlan because
      // beginGroup is not re-entrant: a helper closing its own group would end
      // this one early and split the insert across two undo steps.
      const plan = planRippleInsert(project, {
        trackId,
        atTime: placement.startTime,
        duration,
      });
      const history = actionExecutor.getHistory();
      history.beginGroup("Insert clip");
      try {
        // Split by kind exactly as applyRipplePlan does. Only media lives in
        // track.clips, so clip/move is the wrong instrument for a text or
        // shape clip: the validator rejects the id it cannot find there and
        // the whole insert aborts, after the media ahead of it in this loop
        // has already moved. A track is not guaranteed to hold one kind,
        // because nothing stops a media file being dropped on a text track.
        for (const shift of plan.shifts) {
          if (shift.kind !== "media") {
            get().updateOverlayClipTiming(shift.id, {
              startTime: shift.toStartTime,
            });
            continue;
          }
          const { project: current } = get();
          const move: Action = {
            type: "clip/move",
            id: uuidv4(),
            timestamp: Date.now(),
            params: { clipId: shift.id, startTime: shift.toStartTime },
          };
          const moveResult = await actionExecutor.execute(move, current);
          if (!moveResult.success) return moveResult;
          set({ project: { ...current } });
        }

        const projectCopy = structuredClone(get().project);
        const result = await actionExecutor.execute(action, projectCopy);
        if (result.success) {
          set({ project: { ...projectCopy, modifiedAt: Date.now() } });
        }
        return result;
      } finally {
        history.endGroup();
      }
    },

    /**
     * Adding from the Media panel used to mean "make a new track and put the
     * clip at the playhead". Two clips added that way landed at the same time
     * on Video 1 and Video 2 rather than end to end, and with the playhead
     * parked past the end of a track it left a gap instead. Either way no two
     * clips were ever adjacent, so transitions had nothing to attach to and
     * reported "Clips must be adjacent to add a transition".
     *
     * So this appends. A drop aimed at a specific lane still goes through
     * addClip and keeps the position the user chose.
     */
    addClipToTimeline: async (mediaId: string) => {
      const { project, getMediaItem, actionExecutor } = get();
      const mediaItem = getMediaItem(mediaId);
      if (!mediaItem) {
        return {
          success: false,
          error: {
            code: "MEDIA_NOT_FOUND" as const,
            message: "Media item not found",
          },
        };
      }

      // canAcceptMediaType is what keeps audio off video tracks, and it lets
      // images ride on a video track the same way a drop does.
      const target = project.timeline.tracks.find(
        (track) =>
          !track.locked &&
          (mediaItem.type === "video" ||
            mediaItem.type === "audio" ||
            mediaItem.type === "image") &&
          canAcceptMediaType(track, mediaItem.type),
      );
      // Start of the new track, not the end of the whole project: music added
      // under an existing video belongs at the top of it, not after it.
      if (!target) return get().addClipToNewTrack(mediaId, 0);

      const startTime = target.clips.reduce(
        (end, clip) => Math.max(end, clip.startTime + clip.duration),
        0,
      );

      const projectCopy = structuredClone(project);
      const action: Action = {
        type: "clip/add",
        id: uuidv4(),
        timestamp: Date.now(),
        params: {
          trackId: target.id,
          mediaId,
          startTime,
          // undefined for everything but audio, which leaves clip/add to pick
          // the media's own length as it always has.
          duration: resolveAudioTrimToVideo(mediaItem, project),
        },
      };
      const result = await actionExecutor.execute(action, projectCopy);
      if (result.success) {
        set({ project: { ...projectCopy, modifiedAt: Date.now() } });
      }
      return result;
    },

    addClipToNewTrack: async (
      mediaId: string,
      startTime?: number,
      duration?: number,
    ) => {
      const { project, addTrack, getMediaItem } = get();
      const mediaItem = getMediaItem(mediaId);
      if (!mediaItem) {
        return {
          success: false,
          error: {
            code: "MEDIA_NOT_FOUND" as const,
            message: "Media item not found",
          },
        };
      }

      let trackType: "video" | "audio" | "image" | "text" | "graphics";
      if (mediaItem.type === "video") trackType = "video";
      else if (mediaItem.type === "audio") trackType = "audio";
      else if (mediaItem.type === "image") trackType = "image";
      else trackType = "video";

      // Audio with no chosen position is a bed: music and narration play OVER
      // the footage, so they start where the footage does. Appending them past
      // the end of the timeline, which is right for a video clip, puts them
      // somewhere the playhead never reaches, and trimming them to the video
      // length then only makes a shorter clip in the same unreachable place.
      const appendsAfterEverything = mediaItem.type !== "audio";
      const clipStartTime =
        startTime !== undefined
          ? startTime
          : appendsAfterEverything
            ? calculateTimelineDuration(project)
            : 0;

      // Which ids existed before tells us which one is ours afterwards. The
      // search here used to be for the first EMPTY track of the type, so any
      // empty track already lying around claimed the clip and the track just
      // created was left behind.
      const idsBefore = new Set(project.timeline.tracks.map((t) => t.id));

      const trackResult = await addTrack(trackType);
      if (!trackResult.success) return trackResult;

      const { project: updatedProject, actionExecutor: exec } = get();
      const newTrack = updatedProject.timeline.tracks.find(
        (t) => t.type === trackType && !idsBefore.has(t.id),
      );
      if (!newTrack) {
        return {
          success: false,
          error: {
            code: "TRACK_NOT_FOUND" as const,
            message: "Could not find newly created track",
          },
        };
      }

      const projectCopy = structuredClone(updatedProject);
      const action: Action = {
        type: "clip/add",
        id: uuidv4(),
        timestamp: Date.now(),
        // A caller that asked for a length gets it. Otherwise audio comes back
        // trimmed to the footage, and duration left undefined lets clip/add
        // pick the media's own length, or the 5 second default for images and
        // graphics.
        params: {
          trackId: newTrack.id,
          mediaId,
          startTime: clipStartTime,
          duration:
            duration ?? resolveAudioTrimToVideo(mediaItem, updatedProject),
        },
      };
      const result = await exec.execute(action, projectCopy);
      if (result.success) {
        set({ project: { ...projectCopy, modifiedAt: Date.now() } });
      }
      return result;
    },

    separateAudio: async (clipId: string) => {
      const { project, actionExecutor } = get();
      const videoClip = project.timeline.tracks
        .flatMap((t) => t.clips)
        .find((c) => c.id === clipId);
      if (!videoClip) {
        return {
          success: false,
          error: { code: "CLIP_NOT_FOUND" as const, message: "Clip not found" },
        };
      }

      const mediaItem = project.mediaLibrary.items.find(
        (m) => m.id === videoClip.mediaId,
      );
      if (
        !mediaItem ||
        mediaItem.type !== "video" ||
        !mediaItem.metadata?.channels ||
        mediaItem.metadata.channels === 0
      ) {
        return {
          success: false,
          error: {
            code: "MEDIA_NOT_FOUND" as const,
            message: "Media has no audio to separate",
          },
        };
      }

      let audioTrackCount = mediaItem.metadata.audioTrackCount ?? 1;
      if (audioTrackCount <= 1 && mediaItem.blob) {
        try {
          const { getFFmpegFallback } = await import("@openreel/core/media");
          const ffmpeg = getFFmpegFallback();
          const probeResult = await ffmpeg.probeAudioStreams(mediaItem.blob);
          if (probeResult.audioStreamCount > 1) {
            audioTrackCount = probeResult.audioStreamCount;
          }
        } catch {
          // FFmpeg probe unavailable: proceed with count of 1
        }
      }

      const projectCopy = structuredClone(project);
      const existingAudioCount = projectCopy.timeline.tracks.filter(
        (t) => t.type === "audio",
      ).length;

      const newTrackIds: string[] = [];
      for (let i = existingAudioCount; i < audioTrackCount; i++) {
        const newTrackId = uuidv4();
        newTrackIds.push(newTrackId);
        const trackAction: Action = {
          type: "track/add",
          id: uuidv4(),
          timestamp: Date.now(),
          params: { trackType: "audio", trackId: newTrackId },
        };
        const trackResult = await actionExecutor.execute(
          trackAction,
          projectCopy,
        );
        if (!trackResult.success) {
          return {
            success: false,
            error: {
              code: "TRACK_NOT_FOUND" as const,
              message: "Failed to create audio track",
            },
          };
        }
      }

      const audioTimelineTracks = projectCopy.timeline.tracks.filter(
        (t) => t.type === "audio",
      );
      if (audioTimelineTracks.length === 0) {
        return {
          success: false,
          error: {
            code: "TRACK_NOT_FOUND" as const,
            message: "Could not find or create audio track",
          },
        };
      }

      let lastResult: ActionResult = { success: true };
      for (let trackIdx = 0; trackIdx < audioTrackCount; trackIdx++) {
        const targetTrack = audioTimelineTracks[trackIdx];
        if (!targetTrack) break;
        const action: Action = {
          type: "clip/add",
          id: uuidv4(),
          timestamp: Date.now(),
          params: {
            trackId: targetTrack.id,
            mediaId: videoClip.mediaId,
            startTime: videoClip.startTime,
            audioTrackIndex: trackIdx,
          },
        };
        lastResult = await actionExecutor.execute(action, projectCopy);
        if (!lastResult.success) break;
      }

      if (lastResult.success) {
        for (const track of projectCopy.timeline.tracks) {
          const clipIndex = track.clips.findIndex((c) => c.id === clipId);
          if (clipIndex !== -1) {
            (track.clips[clipIndex] as unknown as { volume: number }).volume =
              0;
            break;
          }
        }
        set({ project: { ...projectCopy, modifiedAt: Date.now() } });
      }
      return lastResult;
    },

    removeClip: async (clipId: string) => {
      const { project, actionExecutor } = get();
      const action: Action = {
        type: "clip/remove",
        id: uuidv4(),
        timestamp: Date.now(),
        params: { clipId },
      };
      const result = await actionExecutor.execute(action, project);
      if (result.success) set({ project: { ...project } });
      return result;
    },

    moveClip: async (clipId: string, startTime: number, trackId?: string) => {
      const { project, actionExecutor } = get();
      const action: Action = {
        type: "clip/move",
        id: uuidv4(),
        timestamp: Date.now(),
        params: { clipId, startTime, trackId },
      };
      const result = await actionExecutor.execute(action, project);
      if (result.success) set({ project: { ...project } });
      return result;
    },

    closeGapBeforeClip: async (clipId: string) => {
      const { project, actionExecutor } = get();
      const action: Action = {
        type: "clip/closeGapBefore",
        id: uuidv4(),
        timestamp: Date.now(),
        params: { clipId },
      };
      const result = await actionExecutor.execute(action, project);
      if (result.success) set({ project: { ...project } });
      return result;
    },

    moveClips: async (moves) => {
      if (moves.length === 0) return { success: true };
      if (moves.length === 1) {
        return get().moveClip(
          moves[0].clipId,
          moves[0].startTime,
          moves[0].trackId,
        );
      }
      const { actionExecutor } = get();
      const history = actionExecutor.getHistory();
      history.beginGroup("Move clips");
      try {
        let lastResult: ActionResult = { success: true };
        for (const move of moves) {
          const { project } = get();
          const action: Action = {
            type: "clip/move",
            id: uuidv4(),
            timestamp: Date.now(),
            params: {
              clipId: move.clipId,
              startTime: move.startTime,
              trackId: move.trackId,
            },
          };
          lastResult = await actionExecutor.execute(action, project);
          if (!lastResult.success) break;
          set({ project: { ...project } });
        }
        return lastResult;
      } finally {
        history.endGroup();
      }
    },

    trimClip: async (clipId: string, inPoint?: number, outPoint?: number) => {
      const { project, actionExecutor } = get();
      const action: Action = {
        type: "clip/trim",
        id: uuidv4(),
        timestamp: Date.now(),
        params: { clipId, inPoint, outPoint },
      };
      const result = await actionExecutor.execute(action, project);
      if (result.success) set({ project: { ...project } });
      return result;
    },

    splitClip: async (clipId: string, time: number) => {
      const { project, actionExecutor } = get();
      const action: Action = {
        type: "clip/split",
        id: uuidv4(),
        timestamp: Date.now(),
        params: { clipId, time },
      };
      const result = await actionExecutor.execute(action, project);
      if (result.success) set({ project: { ...project } });
      return result;
    },

    rippleDeleteClip: async (clipId: string) => {
      const { project, actionExecutor } = get();
      const action: Action = {
        type: "clip/rippleDelete",
        id: uuidv4(),
        timestamp: Date.now(),
        params: { clipId },
      };
      const result = await actionExecutor.execute(action, project);
      if (result.success) set({ project: { ...project } });
      return result;
    },

    slipClip: async (clipId: string, delta: number) => {
      const { project, actionExecutor } = get();
      const action: Action = {
        type: "clip/slip",
        id: uuidv4(),
        timestamp: Date.now(),
        params: { clipId, delta },
      };
      const result = await actionExecutor.execute(action, project);
      if (result.success) set({ project: { ...project } });
      return result;
    },

    slideClip: async (clipId: string, delta: number) => {
      const { project, actionExecutor, getClip } = get();
      const clip = getClip(clipId);
      if (!clip) {
        return {
          success: false,
          error: { code: "INVALID_PARAMS" as const, message: "Clip not found" },
        };
      }
      const track = project.timeline.tracks.find((t) =>
        t.clips.some((c) => c.id === clipId),
      );
      if (!track) {
        return {
          success: false,
          error: {
            code: "INVALID_PARAMS" as const,
            message: "Track not found",
          },
        };
      }
      const sortedClips = [...track.clips].sort(
        (a, b) => a.startTime - b.startTime,
      );
      const clipIndex = sortedClips.findIndex((c) => c.id === clipId);
      const prevClip = clipIndex > 0 ? sortedClips[clipIndex - 1] : undefined;
      const nextClip =
        clipIndex < sortedClips.length - 1
          ? sortedClips[clipIndex + 1]
          : undefined;

      const action: Action = {
        type: "clip/slide",
        id: uuidv4(),
        timestamp: Date.now(),
        params: {
          clipId,
          delta,
          prevClipId: prevClip?.id,
          nextClipId: nextClip?.id,
        },
      };
      const result = await actionExecutor.execute(action, project);
      if (result.success) set({ project: { ...project } });
      return result;
    },

    rollEdit: async (
      leftClipId: string,
      rightClipId: string,
      delta: number,
    ) => {
      const { project, actionExecutor } = get();
      const action: Action = {
        type: "clip/roll",
        id: uuidv4(),
        timestamp: Date.now(),
        params: { leftClipId, rightClipId, delta },
      };
      const result = await actionExecutor.execute(action, project);
      if (result.success) set({ project: { ...project } });
      return result;
    },

    trimToPlayhead: async (
      clipId: string,
      playheadTime: number,
      trimStart: boolean,
    ) => {
      const { project, actionExecutor } = get();
      const action: Action = {
        type: "clip/trimToPlayhead",
        id: uuidv4(),
        timestamp: Date.now(),
        params: { clipId, playheadTime, trimStart },
      };
      const result = await actionExecutor.execute(action, project);
      if (result.success) set({ project: { ...project } });
      return result;
    },

    getClip: (clipId: string) => {
      const { project } = get();
      for (const track of project.timeline.tracks) {
        const clip = track.clips.find((c) => c.id === clipId);
        if (clip) return clip;
      }
      return undefined;
    },
  };
}
